import React, { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Check } from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import {
  marketingColors as colors,
  marketingBrand,
  PAPER,
  DARK,
  BOOKING_CALENDAR_URL,
  marketingEase as ease,
} from '../lib/marketingBrand'
import {
  ACCENT,
  HeroEmboss,
  HudsonOfferBanner,
  MKImageFrame,
  MKRule,
  PaperTextureOverlay,
  ScreenedBg,
} from '../components/marketing/MarketingChrome'

const scrollSection = 'scroll-mt-24 lg:scroll-mt-28'

const JUMP_LINKS = [
  { id: 'membership', label: 'Membership' },
  { id: 'seasonal', label: 'Seasonal' },
  { id: 'smartstart', label: 'SmartStart' },
  { id: 'refresh', label: 'Refresh' },
  { id: 'prelisting', label: 'Pre-listing' },
] as const

const PricingJumpNav: React.FC = () => (
  <nav
    className="sticky top-0 z-30 border-b border-black/[0.06] backdrop-blur-md"
    style={{ backgroundColor: `${PAPER}eb` }}
    aria-label="Pricing sections"
  >
    <div className="max-w-[1400px] mx-auto px-4 md:px-10 lg:px-16 py-2.5 overflow-x-auto">
      <ul className="flex gap-1 sm:gap-2 min-w-max font-sans text-[10px] sm:text-xs font-semibold uppercase tracking-[0.14em]">
        {JUMP_LINKS.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className="inline-block px-2.5 sm:px-3 py-2 rounded-sm whitespace-nowrap transition-colors hover:bg-black/[0.04]"
              style={{ color: colors.tealDark }}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </nav>
)

const Hero: React.FC = () => (
  <section className="relative pt-12 lg:pt-16 pb-16 lg:pb-24 overflow-hidden" style={{ backgroundColor: PAPER }}>
    <PaperTextureOverlay opacity={0.04} />
    <div className="absolute inset-0 opacity-[0.04]" style={{ background: `linear-gradient(135deg, ${colors.copper} 0%, transparent 55%)` }} />
    <HeroEmboss variant="estate" />
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-20 relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-sans text-xs sm:text-sm font-semibold uppercase tracking-[0.28em] mb-5"
          style={{ color: colors.copper }}
        >
          Plans &amp; Investment
        </motion.p>
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 0.85, delay: 0.08, ease }}
          className="origin-left mb-6"
        >
          <MKRule wide />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease }}
          className="font-serif font-normal"
          style={{ fontSize: 'clamp(2.25rem, 5vw, 3.5rem)', lineHeight: 1.08, letterSpacing: '-0.02em', color: colors.tealDark }}
        >
          Pick the Plan That&apos;s{' '}
          <span className="italic" style={{ color: colors.copper }}>
            Right for You
          </span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 font-sans text-base sm:text-lg leading-[1.85] max-w-xl"
          style={{ color: colors.drillbitSkies }}
        >
          We maintain your home so you don&apos;t have to. Jump to ongoing membership, a one-time move-in reset (SmartStart),
          seasonal care, or pre-listing help.
        </motion.p>
      </div>
      <motion.div
        initial={{ opacity: 0, x: 24 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.75, delay: 0.15, ease }}
        className="hidden lg:flex justify-end"
      >
        <MKImageFrame
          src={ACCENT.hudsonConsultation}
          alt="Hudson home consultation"
          aspect="aspect-[4/5]"
          className="max-w-[min(100%,380px)] w-full"
        />
      </motion.div>
    </div>
  </section>
)

const MONTHLY_FEATURES = [
  'Dedicated Hudson Home Specialist',
  'Monthly in-home maintenance visits',
  'MyHome Blueprint\u2122 \u2014 5-year maintenance plan',
  "Proactive care: we don\u2019t wait for things to break",
  'Vendor coordination & transparent pricing for repairs',
  'Same-day response for urgent issues (MyHome Guarantee\u2122)',
]

const QUARTERLY_FEATURES = [
  'Quarterly in-home maintenance visits',
  'MyHome Blueprint\u2122 and full assessment',
  'Ideal for second homes or lighter schedules',
  'Same white-glove care, flexible cadence',
]

const HudsonMembershipMonthly: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  const tiers = [
    { sqft: '< 1,500 sq ft', price: '$399', cadence: '/mo', highlight: true },
    { sqft: '1,501 \u2013 2,999 sq ft', price: '$449', cadence: '/mo', highlight: false },
    { sqft: '3,000 \u2013 4,500 sq ft', price: '$549', cadence: '/mo', highlight: false },
    { sqft: '4,501+ sq ft', price: 'Custom', cadence: 'pricing', highlight: false },
  ]

  return (
    <section
      ref={ref}
      id="membership"
      className={`relative py-28 md:py-36 lg:py-48 overflow-hidden ${scrollSection}`}
      style={{ backgroundColor: PAPER }}
    >
      <PaperTextureOverlay opacity={0.03} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)] gap-12 lg:gap-16 items-start mb-14">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease }}
          >
            <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: colors.copper }}>
              Ongoing care
            </p>
            <h2
              className="font-serif font-normal"
              style={{
                fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
                lineHeight: 1.12,
                letterSpacing: '-0.02em',
                color: colors.tealDark,
              }}
            >
              Hudson Membership (Monthly)
            </h2>
            <p className="mt-4 font-sans text-base leading-[1.85] max-w-2xl" style={{ color: colors.drillbitSkies }}>
              Your dedicated Hudson and scheduled in-home visits—proactive maintenance, blueprint planning, and vendor
              coordination. Every tier receives the same membership benefits; price reflects home size.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1, ease }}
            className="hidden lg:block"
          >
            <MKImageFrame
              src={ACCENT.houseCrosssection}
              alt="Whole-home care, system by system"
              aspect="aspect-[4/3]"
              className="w-full"
            />
            <p className="mt-3 font-sans text-xs uppercase tracking-[0.12em]" style={{ color: colors.drillbitSkies, opacity: 0.75 }}>
              Whole-home care, system by system
            </p>
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.sqft}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: i * 0.1 }}
              className="border-2 overflow-hidden flex flex-col"
              style={{ borderColor: tier.highlight ? colors.teal : colors.plaster, backgroundColor: marketingBrand.white }}
            >
              <div className="p-6 lg:p-7 flex flex-col flex-1">
                {tier.highlight && (
                  <span
                    className="text-[11px] tracking-[0.14em] uppercase font-semibold px-2.5 py-1 text-white mb-4 inline-block self-start"
                    style={{ backgroundColor: colors.teal }}
                  >
                    Most popular
                  </span>
                )}
                <p className="font-sans text-sm mb-1" style={{ color: colors.drillbitSkies }}>
                  {tier.sqft}
                </p>
                <div className="flex items-baseline gap-1 mb-4">
                  <span className="font-serif text-3xl" style={{ color: colors.teal }}>
                    {tier.price}
                  </span>
                  <span className="text-sm" style={{ color: colors.drillbitSkies, opacity: 0.7 }}>
                    {tier.cadence}
                  </span>
                </div>
                <p className="font-sans text-sm leading-relaxed mb-6 flex-1" style={{ color: colors.drillbitSkies }}>
                  Full membership for this home size. See{' '}
                  <a href="#membership-includes" className="underline underline-offset-2 font-medium" style={{ color: colors.teal }}>
                    everything included
                  </a>
                  .
                </p>
                <Link
                  to="/membership"
                  className="block w-full py-3.5 font-sans font-semibold text-white text-center text-sm sm:text-base transition-opacity hover:opacity-90"
                  style={{ backgroundColor: tier.highlight ? colors.copper : colors.teal }}
                >
                  Discover Home Membership
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          id="membership-includes"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease, delay: 0.25 }}
          className={`mt-12 lg:mt-14 border p-8 lg:p-10 ${scrollSection}`}
          style={{ borderColor: colors.plaster, backgroundColor: marketingBrand.white }}
        >
          <p className="font-sans text-sm font-semibold uppercase tracking-[0.18em] mb-6" style={{ color: colors.copper }}>
            Included in every monthly membership
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {MONTHLY_FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 font-sans text-[15px] leading-snug" style={{ color: colors.drillbitSkies }}>
                <Check className="w-5 h-5 shrink-0 mt-0.5" style={{ color: colors.teal }} />
                {f}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  )
}

const HudsonSeasonalQuarterly: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} id="seasonal" className={`relative py-20 lg:py-28 overflow-hidden ${scrollSection}`} style={{ backgroundColor: colors.cream }}>
      <PaperTextureOverlay opacity={0.025} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease }}
          >
            <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: colors.copper }}>
              Lighter cadence
            </p>
            <h2
              className="font-serif font-normal mb-4"
              style={{
                fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                lineHeight: 1.12,
                letterSpacing: '-0.02em',
                color: colors.tealDark,
              }}
            >
              Hudson Seasonal Membership (Quarterly)
            </h2>
            <p className="font-sans text-base leading-[1.85] mb-6" style={{ color: colors.drillbitSkies }}>
              Same Hudson relationship and blueprint-driven care as monthly membership, with visits every quarter—ideal for second
              homes or a lighter schedule.
            </p>
            <div className="flex items-baseline gap-2 mb-8">
              <span className="font-serif text-3xl" style={{ color: colors.teal }}>
                Starting at $195
              </span>
              <span className="text-sm" style={{ color: colors.drillbitSkies, opacity: 0.75 }}>
                / quarter
              </span>
            </div>
            <ul className="space-y-3 mb-8">
              {QUARTERLY_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 font-sans text-base" style={{ color: colors.drillbitSkies }}>
                  <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: colors.teal }} />
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/membership"
              className="group inline-flex items-center justify-center gap-2 py-3.5 px-8 font-sans font-semibold text-white text-base transition-opacity hover:opacity-90"
              style={{ backgroundColor: colors.teal }}
            >
              Discover Home Membership
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.12, ease }}
            className="flex justify-center lg:justify-end"
          >
            <MKImageFrame
              src={ACCENT.watchLuxury}
              alt=""
              aspect="aspect-[4/3]"
              className="max-w-md w-full"
            />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const SmartStartSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} id="smartstart" className={`relative py-28 md:py-36 lg:py-48 overflow-hidden ${scrollSection}`} style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.035} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease }}
          >
            <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: colors.copper }}>
              One-time move-in reset
            </p>
            <h2
              className="font-serif font-normal"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.12, letterSpacing: '-0.02em', color: colors.tealDark }}
            >
              MyHome SmartStart™
            </h2>
            <p className="mt-4 font-sans text-base font-semibold" style={{ color: colors.tealDark }}>
              What it is: a single scheduled visit after closing where a Hudson handles locks, detectors, filters, labeled
              shutoffs, and core safety checks—so the home is yours, not a mystery.
            </p>
            <p className="mt-3 font-sans text-base leading-[1.85]" style={{ color: colors.drillbitSkies }}>
              For buyers moving into an existing home, and for agents who want a closing gift that actually protects the client.
              Two package levels below; full task lists live on the{' '}
              <Link to="/smart-start" className="underline font-medium" style={{ color: colors.teal }}>
                SmartStart page
              </Link>
              .
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1, ease }}
            className="flex justify-center lg:justify-end"
          >
            <ScreenedBg
              src={ACCENT.clipboardChecklist}
              blend="multiply"
              opacity={0.8}
              className="w-full max-w-[280px]"
            />
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease }}
            className="border p-6 lg:p-8"
            style={{ borderColor: colors.plaster, backgroundColor: marketingBrand.white }}
          >
            <h3
              className="font-serif font-normal mb-2"
              style={{ fontSize: '1.25rem', letterSpacing: '-0.02em', color: colors.tealDark }}
            >
              Essential Package
            </h3>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="font-serif text-3xl" style={{ color: colors.teal }}>$299</span>
            </div>
            <p className="font-sans text-base mb-6" style={{ color: colors.drillbitSkies }}>Core safety &amp; security reset for new homeowners.</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={BOOKING_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 py-3 px-6 font-sans font-semibold text-white text-[14px] transition-opacity hover:opacity-90"
                style={{ backgroundColor: colors.copper }}
              >
                Book Your Consultation
              </a>
              <Link
                to="/smart-start"
                className="inline-flex items-center gap-2 py-3 px-6 font-sans font-medium border-2 text-[14px] transition-opacity hover:opacity-80"
                style={{ borderColor: colors.copper, color: colors.copper }}
              >
                Learn More
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease, delay: 0.12 }}
            className="border-2 p-6 lg:p-8 relative"
            style={{ borderColor: colors.copper, backgroundColor: marketingBrand.white }}
          >
            <span
              className="absolute top-0 left-0 right-0 py-1.5 text-center font-sans text-sm font-semibold tracking-[0.12em] uppercase text-white"
              style={{ backgroundColor: colors.copper }}
            >
              Recommended
            </span>
            <div className="pt-6">
              <h3
                className="font-serif font-normal mb-2"
                style={{ fontSize: '1.25rem', letterSpacing: '-0.02em', color: colors.tealDark }}
              >
                Complete Package
              </h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-serif text-3xl" style={{ color: colors.teal }}>$499</span>
              </div>
              <p className="font-sans text-base mb-6" style={{ color: colors.drillbitSkies }}>Full home reset with detailed health report &mdash; nothing left to question.</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={BOOKING_CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 font-sans font-semibold text-white text-[14px] transition-opacity hover:opacity-90"
                  style={{ backgroundColor: colors.teal }}
                >
                  Book Your Consultation
                </a>
                <Link
                  to="/smart-start"
                  className="inline-flex items-center gap-2 py-3 px-6 font-sans font-medium border-2 text-[14px] transition-opacity hover:opacity-80"
                  style={{ borderColor: colors.teal, color: colors.teal }}
                >
                  Learn More
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const MyHomeRefreshSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} id="refresh" className={`relative py-28 md:py-36 lg:py-48 overflow-hidden ${scrollSection}`} style={{ backgroundColor: colors.cream }}>
      <PaperTextureOverlay opacity={0.025} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-[1fr_minmax(0,300px)] gap-10 lg:gap-14 items-start mb-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease }}
          >
            <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: colors.copper }}>
              One-time tune-up (no membership)
            </p>
            <h2
              className="font-serif font-normal"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.12, letterSpacing: '-0.02em', color: colors.tealDark }}
            >
              MyHome Refresh™
            </h2>
            <p className="mt-3 font-sans text-base leading-[1.85] max-w-2xl" style={{ color: colors.drillbitSkies }}>
              A professional home health review and seasonal maintenance visit when you want Hudson expertise without joining
              monthly or quarterly plans.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.08, ease }}
            className="hidden lg:block"
          >
            <MKImageFrame src={ACCENT.hudsonInspecting} alt="" aspect="aspect-square" className="w-full max-w-[280px] ml-auto" />
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease }}
            className="border p-6 lg:p-8"
            style={{ borderColor: colors.plaster, backgroundColor: marketingBrand.white }}
          >
            <h3
              className="font-serif font-normal mb-2"
              style={{ fontSize: '1.25rem', letterSpacing: '-0.02em', color: colors.tealDark }}
            >
              Essential Package
            </h3>
            <div className="flex items-baseline gap-2 mb-4">
              <span className="font-serif text-3xl" style={{ color: colors.teal }}>$299</span>
            </div>
            <p className="font-sans text-base mb-6" style={{ color: colors.drillbitSkies }}>Peace of mind through a one-time proactive tune-up.</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/refresh"
                className="inline-flex items-center gap-2 py-3 px-6 font-sans font-semibold text-white text-[14px] transition-opacity hover:opacity-90"
                style={{ backgroundColor: colors.teal }}
              >
                Learn More
              </Link>
              <a
                href={BOOKING_CALENDAR_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 py-3 px-6 font-sans font-medium border-2 text-[14px] transition-opacity hover:opacity-80"
                style={{ borderColor: colors.copper, color: colors.copper }}
              >
                Book Now
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease, delay: 0.12 }}
            className="border-2 p-6 lg:p-8 relative"
            style={{ borderColor: colors.copper, backgroundColor: marketingBrand.white }}
          >
            <span
              className="absolute top-0 left-0 right-0 py-1.5 text-center font-sans text-sm font-semibold tracking-[0.12em] uppercase text-white"
              style={{ backgroundColor: colors.copper }}
            >
              Recommended
            </span>
            <div className="pt-6">
              <h3
                className="font-serif font-normal mb-2"
                style={{ fontSize: '1.25rem', letterSpacing: '-0.02em', color: colors.tealDark }}
              >
                Complete Package
              </h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="font-serif text-3xl" style={{ color: colors.teal }}>$499</span>
              </div>
              <p className="font-sans text-base mb-6" style={{ color: colors.drillbitSkies }}>In-depth assessment, full preventive tune-up, detailed recommendations.</p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/refresh"
                  className="inline-flex items-center gap-2 py-3 px-6 font-sans font-semibold text-white text-[14px] transition-opacity hover:opacity-90"
                  style={{ backgroundColor: colors.copper }}
                >
                  Learn More
                </Link>
                <a
                  href={BOOKING_CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 font-sans font-medium border-2 text-[14px] transition-opacity hover:opacity-80"
                  style={{ borderColor: colors.teal, color: colors.teal }}
                >
                  Book Now
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

const PreListingSection: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section ref={ref} id="prelisting" className={`relative py-28 md:py-36 lg:py-48 overflow-hidden ${scrollSection}`} style={{ backgroundColor: PAPER }}>
      <PaperTextureOverlay opacity={0.035} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, ease }}
          >
            <p className="font-sans text-sm font-semibold uppercase tracking-[0.15em] mb-3" style={{ color: colors.copper }}>
              For real estate agents
            </p>
            <h2
              className="font-serif font-normal"
              style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', lineHeight: 1.12, letterSpacing: '-0.02em', color: colors.tealDark }}
            >
              Pre-listing / home inspections
            </h2>
            <p className="mt-3 font-sans text-base leading-[1.85] max-w-xl" style={{ color: colors.drillbitSkies }}>
              Help sellers list with confidence—surface issues before they become deal-breakers. Book a consult to scope timing
              and deliverables.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1, ease }}
            className="flex justify-center lg:justify-end"
          >
            <MKImageFrame src={ACCENT.myhomeScoreReport} alt="MyHome Score Report" aspect="aspect-[3/4]" className="w-full max-w-xs" />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease }}
          className="border p-8 lg:p-10 max-w-xl"
          style={{ borderColor: colors.plaster, backgroundColor: marketingBrand.white }}
        >
          <div className="flex items-baseline gap-2 mb-6">
            <span className="font-serif text-3xl" style={{ color: colors.teal }}>Starting at $399</span>
          </div>
          <a
            href={BOOKING_CALENDAR_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 py-3.5 px-8 font-sans font-semibold text-white text-base transition-opacity hover:opacity-90"
            style={{ backgroundColor: colors.copper }}
          >
            Book Now
          </a>
        </motion.div>
      </div>
    </section>
  )
}

const BottomCTA: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-60px' })

  return (
    <section ref={ref} className="relative py-20 lg:py-24 overflow-hidden" style={{ backgroundColor: DARK }}>
      <PaperTextureOverlay opacity={0.02} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col items-stretch justify-between gap-10 lg:flex-row lg:items-center lg:gap-12">
        <motion.div
            initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease }}
            className="text-left"
          >
            <h2
              className="font-serif font-normal text-white"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', lineHeight: 1.08, letterSpacing: '-0.02em' }}
            >
              Ready to Take the Hardship Out of{' '}
              <span className="italic" style={{ color: colors.copper }}>Homeownership?</span>
          </h2>
            <p className="mt-4 font-sans text-base text-white/60">
              Live in your home like a guest in ours.&trade;
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease, delay: 0.12 }}
            className="flex flex-col sm:flex-row shrink-0 gap-4"
          >
            <div className="flex flex-col items-stretch sm:items-center">
              <Link
                to="/membership"
                className="group inline-flex items-center justify-center gap-2 px-10 py-5 font-sans text-base font-bold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: colors.copper }}
              >
              Discover Home Membership
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
              <p className="text-sm font-sans text-white/50 mt-2">No commitment required. Cancel anytime.</p>
            </div>
            <Link
              to="/membership"
              className="inline-flex items-center justify-center gap-2 px-8 py-5 font-sans text-base font-medium border border-white/30 text-white transition-colors hover:bg-white/10"
            >
              Explore Membership
            </Link>
          </motion.div>
          </div>
      </div>
    </section>
  )
}

const PricingPage: React.FC = () => (
    <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
    <MetaTags
      title="Pricing | Hudson My Home"
      description="Hudson My Home pricing: monthly membership from $399, quarterly from $195, MyHome SmartStart one-time move-in reset from $299, Refresh, and pre-listing inspections."
    />
      <Navbar />
      <HudsonOfferBanner />
      <main>
        <Hero />
        <PricingJumpNav />
        <HudsonMembershipMonthly />
        <HudsonSeasonalQuarterly />
        <SmartStartSection />
        <MyHomeRefreshSection />
        <PreListingSection />
        <BottomCTA />
      </main>
      <Footer />
    </div>
  )

export default PricingPage
